/** Name suggestions for the Edit credentials form. Mirrors backend/app/core/credential_names.py (the labels only) —
 *  keep the two in step (a backend test checks the labels). The form accepts free text too; the fit check matches
 *  names strictly, so picking a suggestion is what makes a name match an RFP's requirement. */
export const CERTIFICATE_NAMES: string[] = [
  "CJIS", "ITAR", "FERPA", "COPPA", "HIPAA", "IRS Publication 1075", "42 CFR Part 2", "FedRAMP", "GovRAMP",
  "TX-RAMP", "CMMC", "NIST 800-171", "NIST 800-53", "PCI DSS", "SOC 2", "SOC 1", "ISO 27001", "ISO 9001",
  "ISO 20000", "ISO 14001", "FIPS 140", "Common Criteria", "DoDIN APL", "NSA CSfC", "DISA IL2", "DISA IL4",
  "DISA IL5", "DISA IL6",
];

export const LICENCE_NAMES: string[] = [
  "Professional Engineer (PE)", "Professional Surveyor (PS)", "Registered Architect", "CPA",
  "General contractor licence", "Low-voltage contractor licence",
];

export const BOND_NAMES: string[] = ["Bid bond", "Performance bond", "Payment bond"];

export const INSURANCE_NAMES: string[] = [
  "General liability", "Auto liability", "Workers' compensation", "Professional liability (E&O)",
  "Cyber liability", "Umbrella / excess",
];

export const VEHICLE_NAMES: string[] = [
  "SAM.gov registration", "State vendor registration", "GSA MAS", "OASIS+", "Cooperative contract",
];

export const BUSINESS_CERT_NAMES: string[] = [
  "MBE", "WBE", "DBE", "SBE", "8(a)", "HUBZone", "SDVOSB", "VOSB", "WOSB", "SDB",
];

export type CredentialKind = "licence" | "certificate" | "bond" | "insurance" | "vehicle" | "business_cert";

export const CREDENTIAL_KIND_LABELS: Record<CredentialKind, string> = {
  licence: "Licence", certificate: "Hosting / security certificate", bond: "Bond", insurance: "Insurance",
  vehicle: "Contract vehicle / registration", business_cert: "Business certification",
};

/** Bonds and insurance carry dollar amounts. */
export const AMOUNT_KINDS: CredentialKind[] = ["bond", "insurance"];

export function credentialNameSuggestions(kind: CredentialKind): string[] {
  switch (kind) {
    case "certificate": return CERTIFICATE_NAMES;
    case "bond": return BOND_NAMES;
    case "insurance": return INSURANCE_NAMES;
    case "vehicle": return VEHICLE_NAMES;
    case "business_cert": return BUSINESS_CERT_NAMES;
    default: return LICENCE_NAMES;
  }
}

/** "$2M", "$750K", "$3B", "$1.5M" — the short form shown in the credentials box (the edit form shows exact dollars). */
export function formatUsdShort(value: number | null | undefined): string {
  if (value == null) return "";
  const units: [number, string][] = [[1e9, "B"], [1e6, "M"], [1e3, "K"]];
  for (let i = 0; i < units.length; i++) {
    const [size, suffix] = units[i];
    if (value >= size) {
      const scaled = parseFloat((value / size).toFixed(2));
      // 999,999 must not read "$1000K": roll over into the next unit up
      if (scaled >= 1000 && i > 0) return `$${parseFloat((scaled / 1000).toFixed(2))}${units[i - 1][1]}`;
      return `$${scaled}${suffix}`;
    }
  }
  return `$${value}`;
}

/** "$2M per occurrence · $4M aggregate" (insurance) / "$5M per project · $20M aggregate" (bond); "" when no amount is on file. */
export function amountSummary(kind: CredentialKind, amount?: number | null, aggregate?: number | null): string {
  const per = kind === "bond" ? "per project" : "per occurrence";
  return [amount != null ? `${formatUsdShort(amount)} ${per}` : null, aggregate != null ? `${formatUsdShort(aggregate)} aggregate` : null]
    .filter(Boolean).join(" · ");
}

/** The licences and certificates (the kinds the research gate asks about); the others are advisory. */
export const CORE_KINDS: CredentialKind[] = ["licence", "certificate"];
